import { Color } from "@chessalpha/core";
import type { DialogProps } from "@mui/material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import type { Challenge } from "@ssf/play-common";
import { challengeSchema, defaultTimeControls } from "@ssf/play-common";
import {
  challengesRef,
  onDisconnect,
  push,
  remove,
  serverTimestamp,
} from "@ssf/play-components";
import { useTranslation } from "react-i18next";

import { usePlayer } from "../lib/index.js";

export const ChallengeDialog = ({
  roomKey,
  challengesObj,
  ...props
}: Pick<DialogProps, "open"> & {
  roomKey: string;
  challengesObj?: Record<string, Challenge>;
  onClose: () => void;
}) => {
  const { t } = useTranslation("ChallengeDialog");
  const { player } = usePlayer();

  return (
    <Dialog {...props}>
      <DialogTitle>{t("title")}</DialogTitle>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!player) {
            props.onClose();
            return;
          }

          // Removes old challenges
          for (const [key, challenge] of Object.entries(challengesObj || {})) {
            if (challenge.user.uid === player.uid) {
              remove(challengesRef, roomKey, key);
            }
          }

          // Adds new challenge
          const data = challengeSchema
            .pick({ color: true, timeControl: true })
            .parse(Object.fromEntries(new FormData(event.currentTarget)));
          const challenge: Challenge = {
            ...data,
            user: {
              name: player.name,
              uid: player.uid,
            },
            // @ts-ignore
            createdAt: serverTimestamp(),
          };
          push(
            challengesRef,
            roomKey,
          )(challenge).then((challengeRef) => {
            onDisconnect(challengeRef).remove();
            props.onClose();
          });
        }}
      >
        <Stack spacing={2} sx={{ mx: 3, mb: 1, minWidth: 256 }}>
          <TextField
            name="color"
            label={t("color.label")}
            select
            fullWidth
            defaultValue={0}
          >
            <MenuItem value={0}>{t("color.options.random")}</MenuItem>
            <MenuItem value={Color.white}>{t("color.options.white")}</MenuItem>
            <MenuItem value={Color.black}>{t("color.options.black")}</MenuItem>
          </TextField>
          <TextField
            name="timeControl"
            label={t("time.label")}
            select
            fullWidth
            defaultValue={defaultTimeControls[0]}
          >
            {defaultTimeControls.map((timeControl) => (
              <MenuItem key={timeControl} value={timeControl}>
                {timeControl}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <DialogActions>
          <Button variant="outlined" onClick={props.onClose}>
            {t("close")}
          </Button>
          <Button type="submit" variant="contained" disabled={!player}>
            {t("action")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
